import React, { useEffect, useState } from 'react';
import axios from '../utils/axiosConfig';
import { formatCurrency } from '../utils/currency';
import SearchableProductSelect from '../components/SearchableProductSelect';
import KebabMenu, { MENU_ICONS, useKebabMenu } from '../components/KebabMenu';
import { effectiveSalePrice } from '../utils/uom';

const emptyItem = () => ({ product_id: '', quantity: '1', sell_as: 'piece', uom_id: '' });

function unitChoices(product) {
  const uoms = Array.isArray(product?.uoms) && product.uoms.length
    ? product.uoms
    : [{ id: '', code: product?.unit || 'pcs', is_base: 1, conversion_to_base: 1 }];
  const base = uoms.find((u) => Number(u.is_base)) || uoms[0];
  const piece = uoms.find((u) => !Number(u.is_base) && parseFloat(u.conversion_to_base) > 0 && parseFloat(u.conversion_to_base) < 1) || base;
  const pack = uoms.find((u) => !Number(u.is_base) && parseFloat(u.conversion_to_base) >= 1)
    || (piece && piece !== base ? base : null);
  const choices = [];
  if (piece) choices.push({ key: 'piece', label: `Piece (${piece.code})`, uom: piece });
  if (pack && pack !== piece) choices.push({ key: 'pack', label: `Pack (${pack.code})`, uom: pack });
  return choices;
}

const Combos = () => {
  const [combos, setCombos] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useKebabMenu();
  const [name, setName] = useState('');
  const [price, setPrice] = useState('');
  const [status, setStatus] = useState('active');
  const [items, setItems] = useState([emptyItem(), emptyItem()]);

  const load = async () => {
    setLoading(true);
    try {
      const [comboRes, productRes] = await Promise.all([
        axios.get('/api/combos'),
        axios.get('/api/products')
      ]);
      setCombos(comboRes.data.combos || []);
      setProducts((productRes.data.products || []).filter((p) => p.status === 'active'));
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to load combo offers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditingId(null);
    setName('');
    setPrice('');
    setStatus('active');
    setItems([emptyItem(), emptyItem()]);
  };

  const editCombo = (combo) => {
    setEditingId(combo.id);
    setName(combo.name || '');
    setPrice(combo.price != null ? String(combo.price) : '');
    setStatus(combo.status || 'active');
    const rows = (combo.items || []).map((item) => {
      const product = products.find((p) => p.id === item.product_id);
      const choices = unitChoices(product);
      const match = choices.find((choice) => choice.uom.id && choice.uom.id === item.uom_id);
      return {
        product_id: item.product_id,
        quantity: String(item.quantity),
        sell_as: match?.key || 'piece',
        uom_id: item.uom_id || ''
      };
    });
    setItems(rows.length >= 2 ? rows : [...rows, emptyItem(), emptyItem()].slice(0, 2));
  };

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      name,
      price: parseFloat(price),
      status,
      items: items
        .filter((item) => item.product_id)
        .map((item) => {
          const product = products.find((p) => p.id === item.product_id);
          const choice = unitChoices(product).find((row) => row.key === item.sell_as) || unitChoices(product)[0];
          return {
            product_id: item.product_id,
            quantity: parseFloat(item.quantity) || 1,
            uom_id: choice?.uom?.id || null
          };
        })
    };
    try {
      if (editingId) {
        await axios.put(`/api/combos/${editingId}`, payload);
      } else {
        await axios.post('/api/combos', payload);
      }
      resetForm();
      await load();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save combo');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this combo offer?')) return;
    try {
      await axios.delete(`/api/combos/${id}`);
      if (editingId === id) resetForm();
      await load();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to delete combo');
    }
  };

  const separateTotal = items.reduce((sum, item) => {
    const product = products.find((p) => p.id === item.product_id);
    const qty = parseFloat(item.quantity) || 0;
    const choice = unitChoices(product).find((row) => row.key === item.sell_as) || unitChoices(product)[0];
    return sum + effectiveSalePrice(choice?.uom, product?.price) * qty;
  }, 0);

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1>Combo offers</h1>
        </div>
        <p style={{ marginTop: -12, color: '#64748b' }}>
          Sell two or more products together for one price. POS shows the offer and reduces stock for each product.
        </p>

        <form onSubmit={save} className="card" style={{ padding: 16, marginBottom: 20 }}>
          <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit combo' : 'New combo'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label>Offer name *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required placeholder="Example: Besan + Oil" />
            </div>
            <div className="form-group">
              <label>Combo price *</label>
              <input type="number" min="0" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} required />
            </div>
            <div className="form-group">
              <label>Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} disabled={!editingId}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <label style={{ display: 'block', margin: '8px 0' }}>Products in this offer</label>
          {items.map((item, index) => (
            <div key={index} style={{ display: 'grid', gridTemplateColumns: '2fr 160px 120px auto', gap: 8, marginBottom: 8, alignItems: 'end' }}>
              <SearchableProductSelect
                products={products}
                value={item.product_id}
                onChange={(id) => {
                  const product = products.find((p) => p.id === id);
                  const first = unitChoices(product)[0];
                  setItems((prev) => prev.map((row, i) => (i === index ? {
                    ...row,
                    product_id: id,
                    sell_as: first?.key || 'piece',
                    uom_id: first?.uom?.id || ''
                  } : row)));
                }}
              />
              <select
                value={item.sell_as || 'piece'}
                aria-label="Piece or pack"
                onChange={(e) => setItems((prev) => prev.map((row, i) => (i === index ? { ...row, sell_as: e.target.value } : row)))}
              >
                {unitChoices(products.find((p) => p.id === item.product_id)).map((choice) => (
                  <option key={choice.key} value={choice.key}>{choice.label}</option>
                ))}
              </select>
              <input
                type="number"
                min="0.001"
                step="any"
                value={item.quantity}
                onChange={(e) => setItems((prev) => prev.map((row, i) => (i === index ? { ...row, quantity: e.target.value } : row)))}
                aria-label="Quantity"
              />
              <button
                type="button"
                className="btn btn-danger btn-sm"
                disabled={items.length <= 2}
                onClick={() => setItems((prev) => prev.filter((_, i) => i !== index))}
              >
                Remove
              </button>
            </div>
          ))}
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setItems((prev) => [...prev, emptyItem()])}>
            + Add product
          </button>
          {separateTotal > 0 && (
            <p style={{ fontSize: 13, color: '#64748b' }}>
              Sold separately: {formatCurrency(separateTotal)}
              {price !== '' ? ` · Combo saves ${formatCurrency(Math.max(0, separateTotal - (parseFloat(price) || 0)))}` : ''}
            </p>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="submit" className="btn btn-primary">{editingId ? 'Update offer' : 'Create offer'}</button>
            {editingId && (
              <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
            )}
          </div>
        </form>

        <div className="card" style={{ overflowX: 'auto' }}>
          {loading ? <p style={{ padding: 16 }}>Loading...</p> : (
            <table className="table">
              <thead>
                <tr>
                  <th>Offer</th>
                  <th>Includes</th>
                  <th>Combo price</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {combos.map((combo) => (
                  <tr key={combo.id}>
                    <td>{combo.name}</td>
                    <td>
                      {(combo.items || []).map((item) => `${item.quantity} ${item.uom_code || 'pcs'} × ${item.product_name || 'Product'}`).join(', ') || '—'}
                    </td>
                    <td>{formatCurrency(combo.price)}</td>
                    <td>{combo.status}</td>
                    <td>
                      <KebabMenu
                        menuId={`combo-${combo.id}`}
                        openId={openActionMenu}
                        setOpenId={setOpenActionMenu}
                        items={[
                          { label: 'Edit', icon: MENU_ICONS.edit, onClick: () => editCombo(combo) },
                          { label: 'Delete', icon: MENU_ICONS.trash, danger: true, onClick: () => remove(combo.id) }
                        ]}
                      />
                    </td>
                  </tr>
                ))}
                {!combos.length && (
                  <tr><td colSpan={5}>No combo offers yet</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default Combos;
